import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import 'leaflet/dist/leaflet.css';

const API = '/api/prospects';
function getToken() { return localStorage.getItem('authToken') || ''; }
function headers() { return { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }; }

interface Prospect {
  id: number;
  numero_dpe: string;
  adresse: string | null;
  commune: string | null;
  code_postal: string | null;
  date_etablissement_dpe: string | null;
  etiquette_dpe: string | null;
  surface_habitable: number | null;
  type_batiment: string | null;
  latitude: number | null;
  longitude: number | null;
  dvf_last_sale_date: string | null;
  dvf_last_sale_price: number | null;
  estimation_prix: number | null;
  estimation_confiance: number | null;
  distance_km: number | null;
  status: 'nouveau' | 'contacte' | 'pas_interesse' | 'deja_en_vente';
  created_at: string;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  nouveau:        { label: 'Nouveau',        cls: 'bg-violet-100 text-violet-700 border-violet-200' },
  contacte:       { label: 'Contacté',       cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  pas_interesse:  { label: 'Pas intéressé',  cls: 'bg-gray-100 text-gray-500 border-gray-200' },
  deja_en_vente:  { label: 'Déjà en vente',  cls: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const DPE_COLORS: Record<string, string> = {
  A: 'bg-emerald-500', B: 'bg-green-500', C: 'bg-lime-500', D: 'bg-yellow-400',
  E: 'bg-orange-400',  F: 'bg-orange-600', G: 'bg-red-600',
};

// Mêmes couleurs que DPE_COLORS mais en hex — Leaflet ne comprend pas les classes Tailwind
const DPE_HEX: Record<string, string> = {
  A: '#10b981', B: '#22c55e', C: '#84cc16', D: '#facc15',
  E: '#fb923c', F: '#ea580c', G: '#dc2626',
};

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatPrice(p: number | null) {
  if (!p) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(p);
}

// L'ADEME renvoie des libellés bruts variés (maison, appartement, immeuble...) — on normalise pour l'affichage
function formatTypeBatiment(t: string | null) {
  if (!t) return null;
  const norm = t.toLowerCase();
  if (norm.includes('maison')) return '🏠 Maison';
  if (norm.includes('appartement')) return '🏢 Appartement';
  if (norm.includes('immeuble')) return '🏬 Immeuble';
  return t;
}

interface MarketStat {
  annee: number;
  type: 'Maison' | 'Appartement';
  prixM2Moyen: number;
  prixM2Median: number;
  nombreVentes: number;
}

function formatPriceM2(p: number) {
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(p)} €/m²`;
}

// La donnée utile pour l'agent, ce n'est pas les deux prix séparément mais l'écart entre eux :
// "ce bien a pris/perdu X% depuis sa dernière vente connue" — un vrai argument de prospection.
function computeEvolution(oldPrice: number | null, newPrice: number | null) {
  if (!oldPrice || !newPrice) return null;
  return ((newPrice - oldPrice) / oldPrice) * 100;
}

function EvolutionBadge({ pct, sinceYear }: { pct: number; sinceYear: number | null }) {
  const positif = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${positif ? 'text-emerald-600' : 'text-red-500'}`}>
      {positif ? '▲' : '▼'} {positif ? '+' : ''}{Math.round(pct)}%{sinceYear ? ` depuis ${sinceYear}` : ''}
    </span>
  );
}

function EstimationModal({ prospect, onClose }: { prospect: Prospect; onClose: () => void }) {
  const prixM2 = prospect.surface_habitable && prospect.estimation_prix
    ? Math.round(prospect.estimation_prix / prospect.surface_habitable)
    : null;
  const typeLabel = formatTypeBatiment(prospect.type_batiment) || 'ce type de bien';

  return (
    <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Comment cette estimation est calculée</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
        </div>

        <div className="bg-violet-50 rounded-xl p-4 mb-4 text-center">
          <p className="text-2xl font-bold text-violet-700">{formatPrice(prospect.estimation_prix)}</p>
          <p className="text-xs text-gray-500 mt-1">{prospect.adresse}</p>
        </div>

        <div className="space-y-2 text-sm text-gray-700">
          <div className="flex justify-between border-b border-gray-100 pb-2">
            <span className="text-gray-500">Prix moyen au m² à {prospect.commune}</span>
            <span className="font-medium">{prixM2 ? formatPriceM2(prixM2) : '—'}</span>
          </div>
          <div className="flex justify-between border-b border-gray-100 pb-2">
            <span className="text-gray-500">Surface habitable</span>
            <span className="font-medium">{prospect.surface_habitable ? `${Math.round(prospect.surface_habitable)} m²` : '—'}</span>
          </div>
          <div className="flex justify-between border-b border-gray-100 pb-2">
            <span className="text-gray-500">Calcul</span>
            <span className="font-medium">{prixM2 ? `${formatPriceM2(prixM2)} × ${Math.round(prospect.surface_habitable || 0)} m²` : '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Basé sur</span>
            <span className="font-medium">{prospect.estimation_confiance} vente(s) de {typeLabel.replace(/^[^ ]+ /, '')} à {prospect.commune}</span>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-4 leading-relaxed">
          Prix moyen calculé à partir des ventes réellement enregistrées (fichier DVF, ministère des Finances) sur cette commune. C'est un ordre de grandeur de marché — l'estimation ne tient pas compte de l'état du bien, du terrain, du nombre de pièces ni de la qualité du DPE. À affiner avec une visite.
        </p>
      </div>
    </div>
  );
}

export default function ProspectsPage() {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | Prospect['status']>('all');
  const [forbidden, setForbidden] = useState(false);
  const [marketStats, setMarketStats] = useState<MarketStat[]>([]);
  const [marketLoading, setMarketLoading] = useState(true);
  const [explainProspect, setExplainProspect] = useState<Prospect | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/market`, { headers: headers() });
        const data = await res.json();
        if (data.success) setMarketStats(data.stats || []);
      } catch {
        // silencieux — le graphique ne s'affichera juste pas
      }
      setMarketLoading(false);
    })();
  }, []);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      const res = await fetch(`${API}?${params}`, { headers: headers() });
      if (res.status === 403) { setForbidden(true); setLoading(false); return; }
      const data = await res.json();
      if (data.success) setProspects(data.prospects);
    } catch {
      // silencieux — la page affichera juste "aucun prospect"
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await fetch(`${API}/sync`, { method: 'POST', headers: headers() });
      const data = await res.json();
      if (data.success) {
        setSyncMsg(`✅ ${data.created} nouveau(x) prospect(s) détecté(s) (${data.analyzed} DPE analysés)`);
        load();
      } else {
        setSyncMsg(`❌ ${data.error || 'Erreur lors de la synchronisation'}`);
      }
    } catch {
      setSyncMsg('❌ Erreur réseau');
    }
    setSyncing(false);
  };

  const updateStatus = async (id: number, status: Prospect['status']) => {
    setProspects(prev => prev.map(p => (p.id === id ? { ...p, status } : p)));
    await fetch(`${API}/${id}`, { method: 'PATCH', headers: headers(), body: JSON.stringify({ status }) });
  };

  if (forbidden) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <div className="text-4xl mb-3">🔒</div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Fonctionnalité réservée aux agents SAFTI</h2>
        <p className="text-sm text-gray-500">Cette page n'est pas disponible pour votre profil.</p>
      </div>
    );
  }

  const geoPoints = prospects.filter(p => p.latitude != null && p.longitude != null);

  // Pivot des stats de marché (une ligne par année/type) vers un format exploitable par recharts
  // (une ligne par année, une colonne par type de bien)
  const chartData = (() => {
    const parAnnee = new Map<number, { annee: number; Maison?: number; Appartement?: number }>();
    for (const s of marketStats) {
      if (!parAnnee.has(s.annee)) parAnnee.set(s.annee, { annee: s.annee });
      parAnnee.get(s.annee)![s.type] = s.prixM2Moyen;
    }
    return Array.from(parAnnee.values()).sort((a, b) => a.annee - b.annee);
  })();
  const totalVentesAnalysees = marketStats.reduce((sum, s) => sum + s.nombreVentes, 0);
  const anneeMin = chartData.length ? chartData[0].annee : null;
  const anneeMax = chartData.length ? chartData[chartData.length - 1].annee : null;

  const filterTabs: { id: 'all' | Prospect['status']; label: string }[] = [
    { id: 'all', label: 'Tous' },
    { id: 'nouveau', label: 'Nouveaux' },
    { id: 'contacte', label: 'Contactés' },
    { id: 'pas_interesse', label: 'Pas intéressés' },
    { id: 'deja_en_vente', label: 'Déjà en vente' },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Prospects — DPE récents</h2>
            <p className="text-sm text-gray-500 mt-1 max-w-xl">
              Un DPE fraîchement réalisé sur une adresse de votre secteur est souvent le signe qu'un propriétaire
              prépare une mise en vente ou en location, avant même l'apparition d'une annonce en ligne.
              Ces adresses sont détectées automatiquement chaque semaine via les données publiques ADEME,
              dans un rayon de 10 km autour de votre ville.
            </p>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="shrink-0 px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:from-violet-700 hover:to-indigo-700 disabled:opacity-60 transition-all shadow-sm"
          >
            {syncing ? 'Recherche...' : '🔎 Chercher maintenant'}
          </button>
        </div>
        {syncMsg && <p className="text-sm mt-3">{syncMsg}</p>}
      </div>

      {!marketLoading && chartData.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
            <h3 className="text-sm font-semibold text-gray-900">Évolution du prix au m² sur votre secteur</h3>
            <p className="text-xs text-gray-400">
              {totalVentesAnalysees} vente(s) réelle(s) enregistrée(s) (DVF) analysée(s)
              {anneeMin && anneeMax ? ` — ${anneeMin} à ${anneeMax}` : ''}
            </p>
          </div>
          <p className="text-xs text-gray-500 mb-4 max-w-2xl">
            Calculé à partir des ventes officiellement enregistrées (fichier DVF, ministère des Finances) sur les communes de votre secteur — la même source de données que les grands outils d'estimation du marché.
          </p>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="annee" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${Math.round(v / 1000)}k€`} width={50} />
              <Tooltip formatter={(value: number) => formatPriceM2(value)} labelFormatter={l => `Année ${l}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Maison" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="Appartement" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {filterTabs.map(t => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              filter === t.id
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!loading && geoPoints.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <MapContainer
            center={[geoPoints[0].latitude as number, geoPoints[0].longitude as number]}
            zoom={13}
            style={{ height: 360, width: '100%' }}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {geoPoints.map(p => (
              <CircleMarker
                key={p.id}
                center={[p.latitude as number, p.longitude as number]}
                radius={9}
                pathOptions={{
                  color: '#fff',
                  weight: 2,
                  fillColor: DPE_HEX[p.etiquette_dpe || ''] || '#6b7280',
                  fillOpacity: 0.9,
                }}
              >
                <Popup>
                  <p className="font-semibold">{p.adresse || 'Adresse inconnue'}</p>
                  <p>{p.commune} {p.code_postal}</p>
                  <p>DPE {p.etiquette_dpe || '—'} · {formatDate(p.date_etablissement_dpe)}</p>
                  {p.type_batiment && <p>{formatTypeBatiment(p.type_batiment)}</p>}
                  {p.surface_habitable && <p>{Math.round(p.surface_habitable)} m²</p>}
                  {p.estimation_prix && (
                    <p className="font-semibold text-violet-700">
                      ≈ {formatPrice(p.estimation_prix)} <span className="font-normal text-gray-500">valeur marché</span>
                    </p>
                  )}
                  {p.dvf_last_sale_date && (
                    <p className="mt-1 pt-1 border-t border-gray-200 text-gray-600">
                      Dernière vente : {formatDate(p.dvf_last_sale_date)}
                      {p.dvf_last_sale_price ? ` — ${formatPrice(p.dvf_last_sale_price)}` : ''}
                      {(() => {
                        const pct = computeEvolution(p.dvf_last_sale_price, p.estimation_prix);
                        return pct != null ? <span className={`ml-1 font-semibold ${pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>({pct >= 0 ? '+' : ''}{Math.round(pct)}%)</span> : null;
                      })()}
                    </p>
                  )}
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Chargement...</div>
        ) : prospects.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-3xl mb-2">📭</div>
            <p className="text-sm text-gray-500">Aucun prospect pour le moment. Clique sur "Chercher maintenant" pour lancer une première détection.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-gray-400 text-xs uppercase tracking-wide">
                <th className="px-5 py-3 font-medium">Adresse</th>
                <th className="px-5 py-3 font-medium">Date DPE</th>
                <th className="px-5 py-3 font-medium">Étiquette</th>
                <th className="px-5 py-3 font-medium">Type</th>
                <th className="px-5 py-3 font-medium">Surface</th>
                <th className="px-5 py-3 font-medium">Estimation</th>
                <th className="px-5 py-3 font-medium">Dernière vente (DVF)</th>
                <th className="px-5 py-3 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody>
              {prospects.map(p => (
                <tr key={p.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                  <td className="px-5 py-3">
                    <p className="font-medium text-gray-800">{p.adresse || '—'}</p>
                    <p className="text-xs text-gray-400">
                      {p.commune}{p.code_postal ? ` — ${p.code_postal}` : ''}
                      {p.distance_km != null ? ` · ${p.distance_km < 1 ? '<1' : p.distance_km} km` : ''}
                    </p>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{formatDate(p.date_etablissement_dpe)}</td>
                  <td className="px-5 py-3">
                    {p.etiquette_dpe ? (
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-white text-xs font-bold ${DPE_COLORS[p.etiquette_dpe] || 'bg-gray-400'}`}>
                        {p.etiquette_dpe}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{formatTypeBatiment(p.type_batiment) || '—'}</td>
                  <td className="px-5 py-3 text-gray-600">{p.surface_habitable ? `${Math.round(p.surface_habitable)} m²` : '—'}</td>
                  <td className="px-5 py-3 text-gray-600">
                    {p.estimation_prix ? (
                      <button
                        onClick={() => setExplainProspect(p)}
                        className="text-left hover:bg-violet-50 rounded-lg px-1.5 py-0.5 -mx-1.5 -my-0.5 transition-colors group"
                        title="Voir le détail du calcul"
                      >
                        <p className="font-medium text-gray-800 group-hover:text-violet-700 underline decoration-dotted underline-offset-2">
                          ≈ {formatPrice(p.estimation_prix)}
                        </p>
                        <p className="text-xs text-gray-400">valeur marché aujourd'hui</p>
                      </button>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {p.dvf_last_sale_date ? (
                      <>
                        <p>{formatPrice(p.dvf_last_sale_price)} <span className="text-xs text-gray-400">({formatDate(p.dvf_last_sale_date)})</span></p>
                        {(() => {
                          const pct = computeEvolution(p.dvf_last_sale_price, p.estimation_prix);
                          const year = p.dvf_last_sale_date ? new Date(p.dvf_last_sale_date).getFullYear() : null;
                          return pct != null ? <EvolutionBadge pct={pct} sinceYear={year} /> : null;
                        })()}
                      </>
                    ) : (
                      <span className="text-xs text-gray-300">Aucune donnée</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <select
                      value={p.status}
                      onChange={e => updateStatus(p.id, e.target.value as Prospect['status'])}
                      className={`text-xs font-medium border rounded-lg px-2 py-1 cursor-pointer ${STATUS_LABELS[p.status]?.cls || ''}`}
                    >
                      {Object.entries(STATUS_LABELS).map(([value, { label }]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {explainProspect && <EstimationModal prospect={explainProspect} onClose={() => setExplainProspect(null)} />}
    </div>
  );
}
